import {Chip} from '@dropbox/dig-components/chip';
import {Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {logAiSearchInteraction} from 'analytics/aiSearchAnalytics';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {Employee} from 'client';
import type {ChatMention} from 'components/DSYS/chat/chatMentions';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {useAtomValue, useSetAtom} from 'jotai';

import {profileAskQuestions, profileRelation} from './profileAsk';

/**
 * Ask about this person: one question about them and one about their team,
 * shaped by how they relate to the viewer, shown on others' profiles. The
 * person goes along as a mention, so the server knows exactly who is meant
 * and prefetches what the chip needs.
 */
export const ProfileAskChips = ({
  person,
  reportingLine,
  team,
}: {
  person: ChatMention;
  /** The profile's reporting line, the person first. */
  reportingLine: Pick<Employee, 'ldap'>[];
  /** The profile's main team. */
  team?: string | null;
}) => {
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  const viewer = useAtomValue(loggedInEmployeeAtom);

  // Until the viewer's own profile loads, every relation would read as far.
  if (!viewer.employee.ldap) {
    return null;
  }

  const relation = profileRelation({person, personReportingLine: reportingLine, viewer});
  const questions = profileAskQuestions(person.name, relation, team);

  if (!questions.length) {
    return null;
  }

  return (
    <Split gap="8" wrap="wrap">
      {questions.map(({kind, suggestionKind, label, question}, suggestionIndex) => (
        <Split.Item key={kind}>
          <Chip
            size="small"
            onClick={() => {
              analyticsLogger().logEvent('PROFILE_INTERACTION', {action: 'ask_suggestion'});
              logAiSearchInteraction({
                action: 'suggestion_click',
                source: 'profile',
                suggestionIndex,
                suggestionKind,
              });
              openAiSearchAndAsk(question, 'profile', {mentions: [person], profileAsk: kind});
            }}
          >
            <Chip.IconAccessory>
              <UIIcon src={CompanyAskGlyph} />
            </Chip.IconAccessory>
            <Chip.Content>{label}</Chip.Content>
          </Chip>
        </Split.Item>
      ))}
    </Split>
  );
};
